<template>
    <div class="resource-page__form" :class="{'resource-page__form--ready': isFormReady}">
        <h3 v-if="title" class="resource-page__form__title">{{ title }}</h3>
        <MktHubspotForm
            :portal-id="portalId"
            :form-id="formId"
            :region="region"
            :salesforce-id="salesforceId"
            @ready="onFormReady"
            @submit="onFormSubmit"
        />
        <SocialSharing class="table-social-sharing" />
    </div>
</template>

<script lang="ts" setup>
    import {ref} from 'vue';
    import {MktHubspotForm} from '#components';
    import {useDataTracking} from '#imports';
    import SocialSharing from '~/components/SocialSharing.vue';
    import {useUpdateStickyFormOffset} from '~/composables/useUpdateStickyFormOffset';

    const props = defineProps<{
        title?: string;
        portalId: string;
        formId: string;
        region: string;
        salesforceId: string;
        trackingCategory?: string;
    }>();

    defineEmits(['submit', 'before-ready', 'resize']);

    const {track, extractWebAttributionProperties} = useDataTracking();

    const isFormReady = ref(false);
    const onFormReady = () => {
        isFormReady.value = true;
    };

    /**
     * Tracks the form submission event.
     */
    function onFormSubmit($form: HTMLFormElement) {
        const h1 = document.querySelector('h1');
        const typeField = $form.querySelector(`.hs-input[name=form_type]`) as HTMLInputElement;
        const eventProperties = {
            url: window.location.pathname,
            page_title_h1: h1?.innerText,
            page_category: props.trackingCategory,
            form_type: typeField?.value || null,
            form_name: $form.dataset.formId,
        };
        const userProperties = {
            ...extractWebAttributionProperties(),
            referrer: document.referrer || null,
        };
        track('website_form_submitted', eventProperties, userProperties);
    }

    useUpdateStickyFormOffset();
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .resource-page__form {
        padding: var(--joy-core-spacing-6);
        background-color: var(--joy-color-neutral-0);
        border-radius: var(--joy-core-radius-3);
        border: solid 1px var(--joy-color-neutral-30);
        opacity: 0;
        transition: opacity var(--joy-transition-duration-default) var(--joy-transition-timing-function);

        &__title {
            font-family: var(--joy-font-family-title);
            font-weight: var(--joy-font-weight-bold);
            font-size: var(--joy-font-size-primary-600);
            margin-bottom: var(--joy-core-spacing-6);

            @include mq.screen_md(min) {
                font-size: var(--joy-font-size-secondary-200);
            }
        }

        &--ready {
            opacity: 1;
        }

        /* Themes */
        .resource-page & {
            input[type='submit'] {
                border: 2px solid transparent;
                background-color: var(--joy-color-primary-50);
                &:hover {
                    border: 2px solid transparent;
                    background-color: var(--joy-color-primary-70);
                }
                &:focus {
                    border: 2px solid var(--joy-form-field-focus-color);
                    box-shadow: inset 0 0 0 2px var(--joy-color-neutral-0);
                }
            }
        }

        .resource-page--strategy & {
            input[type='submit'] {
                background-color: var(--joy-color-secondary-30);
                &:hover {
                    background-color: var(--joy-color-secondary-50);
                }
                &:focus {
                    border: 2px solid var(--joy-form-field-focus-color);
                    box-shadow: inset 0 0 0 2px var(--joy-color-neutral-0);
                }
            }
        }
    }
</style>
